import type {
    DropdownData,
    ReactiveFormSupportDataDiagnosticsThreshold,
    ReactiveFormSupportDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormSupportDataSourceFactory } from '@fitogest/reactive-form-builder/utils';
import type { ClienteModel } from 'src/app/models/cliente.model';
import type { ProductosModel } from 'src/app/models/productos/productos.model';
import type { TareaFitoModel } from 'src/app/models/tareas/tarea-fito.model';
import type { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';
import type { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import type { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import type { UserModel } from 'src/app/models/usuarios/user.model';
import type { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';
import type { FitosanitariosEnvironmentDefinition } from '../environment-variables';
import type { FitosanitariosFormDefinition } from './form-control/form-definition';
import {
    FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS,
    FitosanitariosFormLoadPlan,
    type FitosanitariosFormSupportDataKey,
} from './form-control/fitosanitarios-form-load-plan';

export interface FitosanitariosSupportDataEndpoints {
    readonly clientesGET: RequestHandler<ClienteModel[]>;
    readonly tratamientosList: RequestHandler<TratamientosModel[]>;
    readonly justificacionesActuacionGET: RequestHandler<VisitasVariedadMesModel[]>;
    readonly maquinariaGET: RequestHandler<MaquinariaModel[]>;
    readonly trabajadoresGET: RequestHandler<MantenimientoModel[]>;
    readonly usuariosGET: RequestHandler<UserModel[]>;
    readonly productosGET: RequestHandler<ProductosModel[]>;
}

export interface FitosanitariosSupportDataSourcesFactoryContext {
    readonly form: FitosanitariosFormDefinition;
    readonly formType: number;
    readonly env: FitosanitariosEnvironmentDefinition;
    readonly selectedClientId: string;
    readonly endpoints: FitosanitariosSupportDataEndpoints;
}

const FITOSANITARIOS_SUPPORT_DATA_TTL_MS = 300000;
const FITOSANITARIOS_SUPPORT_DATA_STALE_TTL_MS = 86400000;
const FITOSANITARIOS_STABLE_REFERENCE_TTL_MS = 21600000;
const FITOSANITARIOS_STABLE_REFERENCE_STALE_TTL_MS = 604800000;
const FITOSANITARIOS_SUPPORT_DATA_MAX_CACHE_MS = 300;
const FITOSANITARIOS_SUPPORT_DATA_MAX_AVERAGE_MS = 600;
const FITOSANITARIOS_SUPPORT_DATA_MAX_MS = 1800;
const FITOSANITARIOS_SUPPORT_DATA_MAX_NETWORK_MS = 1400;

const FITOSANITARIOS_DEFAULT_DIAGNOSTICS_THRESHOLD: ReactiveFormSupportDataDiagnosticsThreshold = {
    maxAverageMs: FITOSANITARIOS_SUPPORT_DATA_MAX_AVERAGE_MS,
    maxMs: FITOSANITARIOS_SUPPORT_DATA_MAX_MS,
    maxAverageNetworkMs: FITOSANITARIOS_SUPPORT_DATA_MAX_NETWORK_MS,
    maxCacheMs: FITOSANITARIOS_SUPPORT_DATA_MAX_CACHE_MS,
};

const FITOSANITARIOS_SOURCE_THRESHOLDS: Partial<Record<FitosanitariosFormSupportDataKey, ReactiveFormSupportDataDiagnosticsThreshold>> = {
    [FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.tratamientos]: {
        maxAverageMs: 700,
        maxMs: 2200,
        maxAverageNetworkMs: 1800,
        maxCacheMs: FITOSANITARIOS_SUPPORT_DATA_MAX_CACHE_MS,
    },
    [FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.maquinarias]: {
        maxAverageMs: 700,
        maxMs: 2200,
        maxAverageNetworkMs: 1800,
        maxCacheMs: FITOSANITARIOS_SUPPORT_DATA_MAX_CACHE_MS,
    },
    [FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.trabajadores]: {
        maxAverageMs: 700,
        maxMs: 2200,
        maxAverageNetworkMs: 1800,
        maxCacheMs: FITOSANITARIOS_SUPPORT_DATA_MAX_CACHE_MS,
    },
};

/** Creates executable support-data declarations for the Fitosanitarios form. */
export class FitosanitariosSupportDataSourcesFactory {
    private static readonly sourceFactory = new ReactiveFormSupportDataSourceFactory<TareaFitoModel, FitosanitariosFormSupportDataKey>({
        ttlMs: FITOSANITARIOS_SUPPORT_DATA_TTL_MS,
        staleTtlMs: FITOSANITARIOS_SUPPORT_DATA_STALE_TTL_MS,
        cacheStorage: 'persistent',
        staleWhileRevalidate: true,
        diagnosticsThreshold: (key) => FitosanitariosSupportDataSourcesFactory.resolveDiagnosticsThreshold(key),
    });

    public static create(
        context: FitosanitariosSupportDataSourcesFactoryContext,
    ): readonly ReactiveFormSupportDataSource<TareaFitoModel>[] {
        return FitosanitariosFormLoadPlan.createSupportDataSources({
            formType: context.formType,
            env: context.env,
        }).map((source) => FitosanitariosSupportDataSourcesFactory.createSourceForKey(
            context,
            source.key as FitosanitariosFormSupportDataKey,
            source.phase,
        ));
    }

    public static createClientScopedReloadSources(
        context: FitosanitariosSupportDataSourcesFactoryContext,
    ): readonly ReactiveFormSupportDataSource<TareaFitoModel>[] {
        if (!context.env.filterAplicadoresByClient) {
            return [];
        }

        return [
            FitosanitariosSupportDataSourcesFactory.createMaquinariasSource(context, 'deferred'),
            FitosanitariosSupportDataSourcesFactory.createTrabajadoresSource(context, 'deferred'),
        ];
    }

    public static createTratamientosLazySources(
        context: FitosanitariosSupportDataSourcesFactoryContext,
    ): readonly ReactiveFormSupportDataSource<TareaFitoModel>[] {
        return [FitosanitariosSupportDataSourcesFactory.createTratamientosSource(context, 'lazy')];
    }

    private static createSourceForKey(
        context: FitosanitariosSupportDataSourcesFactoryContext,
        key: FitosanitariosFormSupportDataKey,
        phase: ReactiveFormSupportDataSource<TareaFitoModel>['phase'],
    ): ReactiveFormSupportDataSource<TareaFitoModel> {
        switch (key) {
        case FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.clientes:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.clientesGET,
                context.form.lists.clientes,
                { or: ['razon_social', 'nombre'] },
            );
        case FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.tratamientos:
            return this.createTratamientosSource(context, phase);
        case FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.justificaciones:
            return this.createJustificacionesSource(context, phase);
        case FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.maquinarias:
            return this.createMaquinariasSource(context, phase);
        case FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.trabajadores:
            return this.createTrabajadoresSource(context, phase);
        case FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.usuarios:
            return this.createDropdownSource(context, key, phase, context.endpoints.usuariosGET, context.form.lists.usuarios, 'nombre');
        case FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.productos:
            return this.createDropdownSource(context, key, phase, context.endpoints.productosGET, context.form.lists.productos, 'nombre');
        }
    }

    private static createTratamientosSource(
        context: FitosanitariosSupportDataSourcesFactoryContext,
        phase: ReactiveFormSupportDataSource<TareaFitoModel>['phase'],
    ): ReactiveFormSupportDataSource<TareaFitoModel> {
        return this.createDropdownSource(
            context,
            FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.tratamientos,
            phase,
            context.endpoints.tratamientosList,
            context.form.lists.tratamientos,
            'nombre',
            {
                preserveExistingValues: !context.selectedClientId,
                params: this.createTratamientosSummaryParams(context.selectedClientId),
            },
        );
    }

    private static createJustificacionesSource(
        context: FitosanitariosSupportDataSourcesFactoryContext,
        phase: ReactiveFormSupportDataSource<TareaFitoModel>['phase'],
    ): ReactiveFormSupportDataSource<TareaFitoModel> {
        return this.createDropdownSource(
            context,
            FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.justificaciones,
            phase,
            context.endpoints.justificacionesActuacionGET,
            context.form.lists.justificaciones,
            'justificacion',
            {
                ttlMs: FITOSANITARIOS_STABLE_REFERENCE_TTL_MS,
                staleTtlMs: FITOSANITARIOS_STABLE_REFERENCE_STALE_TTL_MS,
            },
        );
    }

    private static createMaquinariasSource(
        context: FitosanitariosSupportDataSourcesFactoryContext,
        phase: ReactiveFormSupportDataSource<TareaFitoModel>['phase'],
    ): ReactiveFormSupportDataSource<TareaFitoModel> {
        const hasClientFilter = context.env.filterAplicadoresByClient && context.selectedClientId !== '';

        return this.createDropdownSource(
            context,
            FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.maquinarias,
            phase,
            context.endpoints.maquinariaGET,
            context.form.lists.maquinarias,
            hasClientFilter ? 'nombre' : (row) => this.createMaquinariaLabel(row),
            {
                preserveExistingValues: !hasClientFilter,
                params: hasClientFilter ? this.createSelectedClientParams(context.selectedClientId) : undefined,
            },
        );
    }

    private static createTrabajadoresSource(
        context: FitosanitariosSupportDataSourcesFactoryContext,
        phase: ReactiveFormSupportDataSource<TareaFitoModel>['phase'],
    ): ReactiveFormSupportDataSource<TareaFitoModel> {
        const hasClientFilter = context.env.filterAplicadoresByClient && context.selectedClientId !== '';

        return this.createDropdownSource(
            context,
            FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.trabajadores,
            phase,
            context.endpoints.trabajadoresGET,
            context.form.lists.trabajadores,
            hasClientFilter ? 'nombre' : (row) => this.createTrabajadorLabel(row),
            {
                noPlaceholder: !hasClientFilter,
                preserveExistingValues: !hasClientFilter,
                params: hasClientFilter ? this.createSelectedClientParams(context.selectedClientId) : undefined,
            },
        );
    }

    private static createDropdownSource(
        context: FitosanitariosSupportDataSourcesFactoryContext,
        key: FitosanitariosFormSupportDataKey,
        phase: ReactiveFormSupportDataSource<TareaFitoModel>['phase'],
        endpoint: RequestHandler<any>,
        target: DropdownData<unknown>,
        label: ReactiveFormSupportDataSource<TareaFitoModel>['label'],
        options: {
            readonly params?: Record<string, unknown> | undefined;
            readonly noPlaceholder?: boolean | undefined;
            readonly preserveExistingValues?: boolean | undefined;
            readonly ttlMs?: number | undefined;
            readonly staleTtlMs?: number | undefined;
            readonly staleWhileRevalidate?: boolean | undefined;
        } = {},
    ): ReactiveFormSupportDataSource<TareaFitoModel> {
        return this.sourceFactory.dropdownSource({
            key,
            phase,
            endpoint,
            target,
            label,
            cacheKey: this.createCacheKey(context, key),
            params: options.params,
            noPlaceholder: options.noPlaceholder,
            preserveExistingValues: options.preserveExistingValues,
            ttlMs: options.ttlMs,
            staleTtlMs: options.staleTtlMs,
            staleWhileRevalidate: options.staleWhileRevalidate,
        });
    }

    private static createSelectedClientParams(selectedClientId: string): Record<string, unknown> | undefined {
        if (selectedClientId === '') {
            return undefined;
        }

        return { id_cliente: selectedClientId };
    }

    private static createTratamientosSummaryParams(selectedClientId: string): Record<string, unknown> {
        const params: Record<string, unknown> = {
            summary: '1',
            limit: '50',
        };

        if (selectedClientId !== '') {
            params['id_cliente'] = selectedClientId;
        }

        return params;
    }

    private static resolveDiagnosticsThreshold(
        key: FitosanitariosFormSupportDataKey,
    ): ReactiveFormSupportDataDiagnosticsThreshold {
        return {
            ...FITOSANITARIOS_DEFAULT_DIAGNOSTICS_THRESHOLD,
            ...(FITOSANITARIOS_SOURCE_THRESHOLDS[key] ?? {}),
        };
    }

    private static createCacheKey(context: FitosanitariosSupportDataSourcesFactoryContext, key: FitosanitariosFormSupportDataKey): string {
        const clientScopedKeys = new Set<FitosanitariosFormSupportDataKey>([
            FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.tratamientos,
            FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.maquinarias,
            FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.trabajadores,
        ]);
        const clientScope = clientScopedKeys.has(key) && context.selectedClientId !== ''
            ? `.client.${context.selectedClientId}`
            : '';

        return `fitosanitarios.${context.env.appName}.${context.formType}.${key}${clientScope}`;
    }

    private static createMaquinariaLabel(row: unknown): string {
        const record = this.toRecord(row);
        const nombre = String(record['nombre'] ?? '');
        const empresa = String(record['empresa'] ?? '').trim();

        return empresa ? `${nombre} - (${empresa})` : nombre;
    }

    private static createTrabajadorLabel(row: unknown): string {
        const record = this.toRecord(row);
        const nombre = String(record['nombre'] ?? '');
        const apellidos = String(record['apellidos'] ?? '');
        const maquinaria = String(record['maquinaria'] ?? '').trim();

        return `${nombre} ${apellidos}${maquinaria ? ` - (${maquinaria})` : ''}`;
    }

    private static toRecord(value: unknown): Record<string, unknown> {
        return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
    }
}
